import { useRef, useLayoutEffect } from 'react';

// Houdt de maandkalenderkaart even hoog tijdens het bladeren tussen maanden.
//
// Bij het wisselen van maand bouwt FullCalendar zijn weergave opnieuw op en is de
// kaart daarbij heel even (1 beeld) tot tientallen pixels lager. Alles eronder (het
// dagoverzicht, de tabbalk-ruimte) schiet dan kort omhoog en omlaag - dat is de
// "flikkering" - en staat de pagina onderaan gescrold, dan wordt de scrollpositie
// door de kortere pagina meegetrokken (de pagina "springt" omhoog).
//
// Oplossing: de hoogte van het huidige paneel wordt gemeten en als minimumhoogte
// vastgehouden (per schermbreedte de grootste gemeten waarde), zodat de kaart nooit
// kleiner wordt dan wat al getoond is. Enkel de inhoud meten en onthouden, nooit de
// minimumhoogte terugzetten naar 0 (dat zou de pagina juist korter maken).
//
// Gebruik: useStableMonthHeight(trackRef, [month, tab, ...])
//   - trackRef: de ref van de "track" uit useMonthSwipe (met daarin
//     ".cleaner-calendar-pane-current").
//   - deps: alles waarbij de kalender (opnieuw) opgebouwd wordt.
export default function useStableMonthHeight(trackRef, deps) {
  const maxH = useRef({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const apply = () => {
      const cur = track.querySelector('.cleaner-calendar-pane-current');
      const inner = cur && cur.firstElementChild;
      if (!cur || !inner) return;
      const w = track.offsetWidth;
      if (maxH.current.w !== w) maxH.current = { w, h: 0 };
      const cs = window.getComputedStyle(cur);
      const padding = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      // Ook de buurmaanden (vorige/volgende, staan al gerenderd naast de huidige)
      // meten: zo is de kaart nu al hoog genoeg voor de volgende maand en schuift
      // de inhoud eronder niet alsnog een paar pixels bij het bladeren.
      // Exacte (kommagetal-)hoogte, NIET naar boven afgerond: een afronding (bv. 545,79 -> 546)
      // liet de kaart bij de allereerste maandwissel 0,2px groeien, waardoor alles eronder
      // net even verschoof. Met de exacte waarde verandert er niets zichtbaars.
      let h = inner.getBoundingClientRect().height + padding;
      track.querySelectorAll('.cleaner-calendar-pane-prev, .cleaner-calendar-pane-next').forEach((pane) => {
        const el = pane.firstElementChild;
        if (el) h = Math.max(h, el.getBoundingClientRect().height + padding);
      });
      if (h > maxH.current.h + 0.05) {
        maxH.current.h = h;
        cur.style.minHeight = `${h}px`;
      } else if (maxH.current.h && cur.style.minHeight !== `${maxH.current.h}px`) {
        cur.style.minHeight = `${maxH.current.h}px`;
      }
    };
    apply();
    const t1 = setTimeout(apply, 60);
    const t2 = setTimeout(apply, 350);
    window.addEventListener('resize', apply);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', apply); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
